/**
 * El backend usa CamelCaseJSONRenderer: las claves llegan en camelCase.
 * Normalizamos a la forma snake_case que usa el módulo Reportes (tipos en api.ts).
 */
import type {
  ReporteVentasGeneralResponse,
  ReporteVentasPorItemResponse,
  ReporteVentasPorVendedorResponse,
  ReporteVentasPorUsuarioDetalleResponse,
  ReportePresupuestosPorUsuarioResponse,
  ReportePresupuestosPorUsuarioDetalleResponse,
  ReporteStockResponse,
  ReportePreciosResponse,
  ReporteClientesDeudoresResponse,
  ReporteChequesResponse,
  ReporteMovimientosCajaResponse,
  ReporteValorizacionStockResponse,
  ReporteRemitosAbiertosResponse,
} from "./api";

function pick<T>(obj: Record<string, unknown>, camel: string, snake: string): T | undefined {
  if (camel in obj) return obj[camel] as T;
  if (snake in obj) return obj[snake] as T;
  return undefined;
}

export function normalizeReporteVentasGeneral(raw: Record<string, unknown>): ReporteVentasGeneralResponse {
  const rows = pick<unknown[]>(raw, "porTipo", "por_tipo") ?? [];
  const por_tipo = rows.map((r) => {
    const row = r as Record<string, unknown>;
    const tipo = String(pick(row, "tipoComprobante", "tipo_comprobante") ?? "");
    const out: {
      tipo_comprobante: string;
      cantidad: number;
      total: string;
      total_bruto: string;
      ticket_promedio: string;
      total_nc_magnitud?: string;
    } = {
      tipo_comprobante: tipo,
      cantidad: Number(pick(row, "cantidad", "cantidad") ?? 0),
      total: String(pick(row, "total", "total") ?? "0"),
      total_bruto: String(pick(row, "totalBruto", "total_bruto") ?? "0"),
      ticket_promedio: String(pick(row, "ticketPromedio", "ticket_promedio") ?? "0"),
    };
    const ncMag = pick<string>(row, "totalNcMagnitud", "total_nc_magnitud");
    if (ncMag != null && ncMag !== "") out.total_nc_magnitud = String(ncMag);
    return out;
  });
  const rubroRows = pick<unknown[]>(raw, "porRubro", "por_rubro") ?? [];
  const porRubro = rubroRows.map((r) => {
    const row = r as Record<string, unknown>;
    return {
      grupo_id: Number(pick(row, "grupoId", "grupo_id") ?? 0),
      grupo_nombre: String(pick(row, "grupoNombre", "grupo_nombre") ?? ""),
      cantidad: Number(pick(row, "cantidad", "cantidad") ?? 0),
      total: String(pick(row, "total", "total") ?? "0"),
      total_bruto: String(pick(row, "totalBruto", "total_bruto") ?? "0"),
      ticket_promedio: String(pick(row, "ticketPromedio", "ticket_promedio") ?? "0"),
    };
  });
  const filtrosRaw = (pick<Record<string, unknown>>(raw, "filtros", "filtros") ?? {}) as Record<
    string,
    unknown
  >;
  const rubrosRaw = pick<unknown[]>(filtrosRaw, "rubros", "rubros") ?? [];
  const rubros = rubrosRaw.map((r) => {
    const row = r as Record<string, unknown>;
    return {
      id: Number(pick(row, "id", "id") ?? 0),
      codigo: String(pick(row, "codigo", "codigo") ?? ""),
      nombre: String(pick(row, "nombre", "nombre") ?? ""),
    };
  });
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    por_tipo,
    por_rubro: porRubro.length ? porRubro : undefined,
    filtros: { rubros },
    total_neto_periodo: String(pick(raw, "totalNetoPeriodo", "total_neto_periodo") ?? "0"),
    total_general: String(
      pick(raw, "totalGeneral", "total_general") ??
        pick(raw, "totalNetoPeriodo", "total_neto_periodo") ??
        "0",
    ),
    total_bruto: String(pick(raw, "totalBruto", "total_bruto") ?? "0"),
    total_nc: String(pick(raw, "totalNc", "total_nc") ?? "0"),
    ticket_promedio: String(pick(raw, "ticketPromedio", "ticket_promedio") ?? "0"),
    cantidad_comprobantes: Number(pick(raw, "cantidadComprobantes", "cantidad_comprobantes") ?? 0),
  };
}

export function normalizeReporteVentasPorItem(raw: Record<string, unknown>): ReporteVentasPorItemResponse {
  const itemsRaw = pick<unknown[]>(raw, "items", "items") ?? [];
  const items = itemsRaw.map((it) => {
    const r = it as Record<string, unknown>;
    return {
      producto_id: (pick(r, "productoId", "producto_id") as number | null) ?? null,
      codigo_producto: String(pick(r, "codigoProducto", "codigo_producto") ?? ""),
      nombre_producto: String(pick(r, "nombreProducto", "nombre_producto") ?? ""),
      cantidad_vendida: String(pick(r, "cantidadVendida", "cantidad_vendida") ?? "0"),
      importe_total: String(pick(r, "importeTotal", "importe_total") ?? "0"),
    };
  });
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    items,
  };
}

export function normalizeReporteVentasPorVendedor(raw: Record<string, unknown>): ReporteVentasPorVendedorResponse {
  const venRaw = pick<unknown[]>(raw, "vendedores", "vendedores") ?? [];
  const vendedores = venRaw.map((v) => {
    const r = v as Record<string, unknown>;
    return {
      usuario_id: (pick(r, "usuarioId", "usuario_id") as number | null) ?? null,
      usuario_username: (pick(r, "usuarioUsername", "usuario_username") as string | null) ?? null,
      usuario_label: String(pick(r, "usuarioLabel", "usuario_label") ?? ""),
      total_vendido: String(pick(r, "totalVendido", "total_vendido") ?? "0"),
      comprobantes: Number(pick(r, "comprobantes", "comprobantes") ?? 0),
      ticket_promedio: String(pick(r, "ticketPromedio", "ticket_promedio") ?? "0"),
    };
  });
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    vendedores,
  };
}

export function normalizeReporteVentasPorUsuarioDetalle(
  raw: Record<string, unknown>
): ReporteVentasPorUsuarioDetalleResponse {
  const facRaw = pick<unknown[]>(raw, "facturas", "facturas") ?? [];
  const facturas = facRaw.map((f) => {
    const r = f as Record<string, unknown>;
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      fecha: (pick(r, "fecha", "fecha") as string | null) ?? null,
      numero: String(pick(r, "numero", "numero") ?? ""),
      tipo_comprobante: String(pick(r, "tipoComprobante", "tipo_comprobante") ?? ""),
      cliente_nombre: String(pick(r, "clienteNombre", "cliente_nombre") ?? ""),
      usuario_label: String(pick(r, "usuarioLabel", "usuario_label") ?? ""),
      estado: String(pick(r, "estado", "estado") ?? ""),
      total: String(pick(r, "total", "total") ?? "0"),
    };
  });
  const totRaw = (pick(raw, "totales", "totales") as Record<string, unknown>) ?? {};
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    usuario_id: (pick(raw, "usuarioId", "usuario_id") as number | null) ?? null,
    usuario_username: (pick(raw, "usuarioUsername", "usuario_username") as string | null) ?? null,
    usuario_label: String(pick(raw, "usuarioLabel", "usuario_label") ?? ""),
    totales: {
      total_vendido: String(pick(totRaw, "totalVendido", "total_vendido") ?? "0"),
      comprobantes: Number(pick(totRaw, "comprobantes", "comprobantes") ?? 0),
      ticket_promedio: String(pick(totRaw, "ticketPromedio", "ticket_promedio") ?? "0"),
    },
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    facturas,
  };
}

export function normalizeReportePresupuestosPorUsuario(
  raw: Record<string, unknown>
): ReportePresupuestosPorUsuarioResponse {
  const rowsRaw = pick<unknown[]>(raw, "usuarios", "usuarios") ?? [];
  const usuarios = rowsRaw.map((v) => {
    const r = v as Record<string, unknown>;
    return {
      usuario_id: (pick(r, "usuarioId", "usuario_id") as number | null) ?? null,
      usuario_username: (pick(r, "usuarioUsername", "usuario_username") as string | null) ?? null,
      usuario_label: String(pick(r, "usuarioLabel", "usuario_label") ?? ""),
      creados: Number(pick(r, "creados", "creados") ?? 0),
      usados: Number(pick(r, "usados", "usados") ?? 0),
      cancelados: Number(pick(r, "cancelados", "cancelados") ?? 0),
    };
  });
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    usuarios,
  };
}

export function normalizeReportePresupuestosPorUsuarioDetalle(
  raw: Record<string, unknown>
): ReportePresupuestosPorUsuarioDetalleResponse {
  const prRaw = pick<unknown[]>(raw, "presupuestos", "presupuestos") ?? [];
  const presupuestos = prRaw.map((f) => {
    const r = f as Record<string, unknown>;
    const eliminadoRaw = pick(r, "eliminado", "eliminado");
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      fecha: (pick(r, "fecha", "fecha") as string | null) ?? null,
      numero: String(pick(r, "numero", "numero") ?? ""),
      cliente_nombre: String(pick(r, "clienteNombre", "cliente_nombre") ?? ""),
      usuario_label: String(pick(r, "usuarioLabel", "usuario_label") ?? ""),
      estado: String(pick(r, "estado", "estado") ?? ""),
      total: String(pick(r, "total", "total") ?? "0"),
      eliminado: Boolean(eliminadoRaw),
    };
  });
  const totRaw = (pick(raw, "totales", "totales") as Record<string, unknown>) ?? {};
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    usuario_id: (pick(raw, "usuarioId", "usuario_id") as number | null) ?? null,
    usuario_username: (pick(raw, "usuarioUsername", "usuario_username") as string | null) ?? null,
    usuario_label: String(pick(raw, "usuarioLabel", "usuario_label") ?? ""),
    totales: {
      creados: Number(pick(totRaw, "creados", "creados") ?? 0),
      usados: Number(pick(totRaw, "usados", "usados") ?? 0),
      cancelados: Number(pick(totRaw, "cancelados", "cancelados") ?? 0),
    },
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    presupuestos,
  };
}

export function normalizeReporteStock(raw: Record<string, unknown>): ReporteStockResponse {
  const vista = (pick(raw, "vista", "vista") as ReporteStockResponse["vista"]) ?? "general";
  const rowsRaw = pick<unknown[]>(raw, "rows", "rows") ?? [];
  const rows = rowsRaw.map((row) => {
    const r = row as Record<string, unknown>;
    if (vista === "fabrica") {
      return {
        proveedor_id: (pick(r, "proveedorId", "proveedor_id") as number | null) ?? null,
        proveedor_codigo: String(pick(r, "proveedorCodigo", "proveedor_codigo") ?? ""),
        proveedor_nombre: String(pick(r, "proveedorNombre", "proveedor_nombre") ?? ""),
        cantidad_total: String(pick(r, "cantidadTotal", "cantidad_total") ?? "0"),
      };
    }
    return {
      producto_id: Number(pick(r, "productoId", "producto_id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      nombre: String(pick(r, "nombre", "nombre") ?? ""),
      cantidad_total: String(pick(r, "cantidadTotal", "cantidad_total") ?? "0"),
    };
  });
  return {
    vista,
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    rows: rows as ReporteStockResponse["rows"],
  };
}

export function normalizeReportePrecios(raw: Record<string, unknown>): ReportePreciosResponse {
  const rowsRaw = pick<unknown[]>(raw, "rows", "rows") ?? [];
  const rows = rowsRaw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      producto_id: Number(pick(r, "productoId", "producto_id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      nombre: String(pick(r, "nombre", "nombre") ?? ""),
      grupo_id: (pick(r, "grupoId", "grupo_id") as number | null) ?? null,
      grupo_nombre: String(pick(r, "grupoNombre", "grupo_nombre") ?? ""),
      proveedor_id: (pick(r, "proveedorId", "proveedor_id") as number | null) ?? null,
      proveedor_nombre: String(pick(r, "proveedorNombre", "proveedor_nombre") ?? ""),
      precio_base: String(pick(r, "precioBase", "precio_base") ?? "0"),
      ganancia_porcentaje: String(pick(r, "gananciaPorcentaje", "ganancia_porcentaje") ?? "0"),
      iva_porcentaje: String(pick(r, "ivaPorcentaje", "iva_porcentaje") ?? "0"),
      precio_final: String(pick(r, "precioFinal", "precio_final") ?? "0"),
      activo: Boolean(pick(r, "activo", "activo") ?? true),
      es_personalizado: Boolean(pick(r, "esPersonalizado", "es_personalizado") ?? false),
    };
  });
  const metaRaw = (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {};
  const totalFilas = Number(pick(metaRaw, "totalFilas", "total_filas") ?? rows.length);
  const page = Number(pick(metaRaw, "page", "page") ?? 1);
  const pageSize = Number(pick(metaRaw, "pageSize", "page_size") ?? rows.length);
  return {
    meta: {
      ...metaRaw,
      total_filas: Number.isFinite(totalFilas) ? totalFilas : rows.length,
      page: Number.isFinite(page) ? page : 1,
      page_size: Number.isFinite(pageSize) ? pageSize : rows.length,
    },
    rows,
  };
}

export function normalizeReporteClientesDeudores(raw: Record<string, unknown>): ReporteClientesDeudoresResponse {
  const cliRaw = pick<unknown[]>(raw, "clientes", "clientes") ?? [];
  const clientes = cliRaw.map((c) => {
    const r = c as Record<string, unknown>;
    return {
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      nombre: String(pick(r, "nombre", "nombre") ?? ""),
      saldo: String(pick(r, "saldo", "saldo") ?? "0"),
    };
  });
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    modo: String(pick(raw, "modo", "modo") ?? ""),
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    clientes,
  };
}

export function normalizeReporteCheques(raw: Record<string, unknown>): ReporteChequesResponse {
  const chRaw = pick<unknown[]>(raw, "cheques", "cheques") ?? [];
  const cheques = chRaw.map((c) => {
    const r = c as Record<string, unknown>;
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      numero_cheque: String(pick(r, "numeroCheque", "numero_cheque") ?? ""),
      banco: String(pick(r, "banco", "banco") ?? ""),
      fecha_emision: String(pick(r, "fechaEmision", "fecha_emision") ?? ""),
      fecha_cobro: (pick(r, "fechaCobro", "fecha_cobro") as string | null) ?? null,
      tipo: (pick(r, "tipo", "tipo") as string | null) ?? null,
      origen: (pick(r, "origen", "origen") as string | null) ?? null,
      importe: String(pick(r, "importe", "importe") ?? "0"),
      moneda: String(pick(r, "moneda", "moneda") ?? ""),
      cobrado: Boolean(pick(r, "cobrado", "cobrado") ?? false),
      estado: String(pick(r, "estado", "estado") ?? ""),
      emisor: (pick(r, "emisor", "emisor") as string | null) ?? null,
    };
  });
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    cheques,
  };
}

export function normalizeReporteMovimientosCaja(
  raw: Record<string, unknown>
): ReporteMovimientosCajaResponse {
  const totRaw = (pick(raw, "totales", "totales") as Record<string, unknown>) ?? {};
  const filRaw = (pick(raw, "filtros", "filtros") as Record<string, unknown>) ?? {};
  const tiposRaw = pick<unknown[]>(filRaw, "tipos", "tipos") ?? [];
  const usuariosRaw = pick<unknown[]>(filRaw, "usuarios", "usuarios") ?? [];
  const movRaw = pick<unknown[]>(raw, "movimientos", "movimientos") ?? [];
  const tipos = tiposRaw.map((t) => {
    const r = t as Record<string, unknown>;
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      nombre: String(pick(r, "nombre", "nombre") ?? ""),
      sentido: String(pick(r, "sentido", "sentido") ?? ""),
    };
  });
  const usuarios = usuariosRaw.map((u) => {
    const r = u as Record<string, unknown>;
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      usuario_label: String(pick(r, "usuarioLabel", "usuario_label") ?? ""),
      usuario_username: (pick(r, "usuarioUsername", "usuario_username") as string | null) ?? null,
    };
  });
  const movimientos = movRaw.map((m) => {
    const r = m as Record<string, unknown>;
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      fecha: (pick(r, "fecha", "fecha") as string | null) ?? null,
      tipo_movimiento_id: (pick(r, "tipoMovimientoId", "tipo_movimiento_id") as number | null) ?? null,
      tipo_movimiento_nombre: String(pick(r, "tipoMovimientoNombre", "tipo_movimiento_nombre") ?? ""),
      sentido: String(pick(r, "sentido", "sentido") ?? ""),
      usuario_id: (pick(r, "usuarioId", "usuario_id") as number | null) ?? null,
      usuario_label: String(pick(r, "usuarioLabel", "usuario_label") ?? ""),
      usuario_username: (pick(r, "usuarioUsername", "usuario_username") as string | null) ?? null,
      created_by_id: (pick(r, "createdById", "created_by_id") as number | null) ?? null,
      created_by_label: String(pick(r, "createdByLabel", "created_by_label") ?? "Sin usuario"),
      usuario_destino_id: (pick(r, "usuarioDestinoId", "usuario_destino_id") as number | null) ?? null,
      usuario_destino_label: String(
        pick(r, "usuarioDestinoLabel", "usuario_destino_label") ??
          pick(r, "usuarioLabel", "usuario_label") ??
          "Sin usuario"
      ),
      usuario_destino_username:
        (pick(r, "usuarioDestinoUsername", "usuario_destino_username") as string | null) ??
        (pick(r, "usuarioUsername", "usuario_username") as string | null) ??
        null,
      importe: String(pick(r, "importe", "importe") ?? "0"),
      importe_signed: String(pick(r, "importeSigned", "importe_signed") ?? "0"),
      observaciones: String(pick(r, "observaciones", "observaciones") ?? ""),
    };
  });
  return {
    desde: String(raw.desde ?? ""),
    hasta: String(raw.hasta ?? ""),
    totales: {
      cantidad: Number(pick(totRaw, "cantidad", "cantidad") ?? 0),
      total_ingresos: String(pick(totRaw, "totalIngresos", "total_ingresos") ?? "0"),
      total_egresos: String(pick(totRaw, "totalEgresos", "total_egresos") ?? "0"),
      total_neto: String(pick(totRaw, "totalNeto", "total_neto") ?? "0"),
    },
    filtros: { tipos, usuarios },
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    movimientos,
  };
}

export function normalizeReporteValorizacionStock(
  raw: Record<string, unknown>
): ReporteValorizacionStockResponse {
  const totRaw = (pick(raw, "totales", "totales") as Record<string, unknown>) ?? {};
  const filRaw = (pick(raw, "filtros", "filtros") as Record<string, unknown>) ?? {};
  const sucRaw = pick<unknown[]>(filRaw, "sucursales", "sucursales") ?? [];
  const depRaw = pick<unknown[]>(filRaw, "depositos", "depositos") ?? [];
  const rowsRaw = pick<unknown[]>(raw, "rows", "rows") ?? [];
  const sucursales = sucRaw.map((s) => {
    const r = s as Record<string, unknown>;
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      nombre: String(pick(r, "nombre", "nombre") ?? ""),
    };
  });
  const depositos = depRaw.map((d) => {
    const r = d as Record<string, unknown>;
    const idsRaw = pick<unknown[]>(r, "sucursalIds", "sucursal_ids") ?? [];
    return {
      id: Number(pick(r, "id", "id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      nombre: String(pick(r, "nombre", "nombre") ?? ""),
      sucursal_ids: (Array.isArray(idsRaw) ? idsRaw : []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0),
    };
  });
  const rows = rowsRaw.map((row) => {
    const r = row as Record<string, unknown>;
    const sucs = pick<unknown[]>(r, "sucursales", "sucursales") ?? [];
    return {
      producto_id: Number(pick(r, "productoId", "producto_id") ?? 0),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      nombre: String(pick(r, "nombre", "nombre") ?? ""),
      deposito_id: Number(pick(r, "depositoId", "deposito_id") ?? 0),
      deposito_codigo: String(pick(r, "depositoCodigo", "deposito_codigo") ?? ""),
      deposito_nombre: String(pick(r, "depositoNombre", "deposito_nombre") ?? ""),
      sucursales: (Array.isArray(sucs) ? sucs : []).map((x) => String(x)),
      cantidad: String(pick(r, "cantidad", "cantidad") ?? "0"),
      precio_base: String(pick(r, "precioBase", "precio_base") ?? "0"),
      valor: String(pick(r, "valor", "valor") ?? "0"),
    };
  });
  const metaRaw = (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {};
  return {
    totales: {
      filas: Number(pick(totRaw, "filas", "filas") ?? rows.length),
      cantidad_total: String(pick(totRaw, "cantidadTotal", "cantidad_total") ?? "0"),
      valor_total: String(pick(totRaw, "valorTotal", "valor_total") ?? "0"),
    },
    filtros: { sucursales, depositos },
    meta: {
      ...metaRaw,
      campo_valor: String(pick(metaRaw, "campoValor", "campo_valor") ?? "precio_base"),
      campo_valor_label: String(
        pick(metaRaw, "campoValorLabel", "campo_valor_label") ?? "Precio base"
      ),
      excluye_reservado: Boolean(pick(metaRaw, "excluyeReservado", "excluye_reservado") ?? true),
    },
    rows,
  };
}

export function normalizeReporteRemitosAbiertos(
  raw: Record<string, unknown>
): ReporteRemitosAbiertosResponse {
  const totRaw = (pick(raw, "totales", "totales") as Record<string, unknown>) ?? {};
  const rowsRaw = pick<unknown[]>(raw, "rows", "rows") ?? [];
  const rows = rowsRaw.map((item) => {
    const r = item as Record<string, unknown>;
    return {
      remito_id: Number(pick(r, "remitoId", "remito_id") ?? 0),
      linea_id: Number(pick(r, "lineaId", "linea_id") ?? 0),
      remito_numero: String(pick(r, "remitoNumero", "remito_numero") ?? ""),
      cliente_codigo: String(pick(r, "clienteCodigo", "cliente_codigo") ?? ""),
      cliente_nombre: String(pick(r, "clienteNombre", "cliente_nombre") ?? ""),
      codigo: String(pick(r, "codigo", "codigo") ?? ""),
      descripcion: String(pick(r, "descripcion", "descripcion") ?? ""),
      cantidad: String(pick(r, "cantidad", "cantidad") ?? "0"),
      entregado: String(pick(r, "entregado", "entregado") ?? "0"),
      pendiente: String(pick(r, "pendiente", "pendiente") ?? "0"),
      dias: Number(pick(r, "dias", "dias") ?? 0),
      tipo: String(pick(r, "tipo", "tipo") ?? ""),
      fecha: (pick(r, "fecha", "fecha") as string | null) ?? null,
      usuario_venta: String(pick(r, "usuarioVenta", "usuario_venta") ?? ""),
      saldo_cliente: String(pick(r, "saldoCliente", "saldo_cliente") ?? "0"),
      estado_remito: String(pick(r, "estadoRemito", "estado_remito") ?? ""),
    };
  });
  return {
    generado: String(raw.generado ?? ""),
    meta: (pick(raw, "meta", "meta") as Record<string, unknown>) ?? {},
    totales: { filas: Number(pick(totRaw, "filas", "filas") ?? rows.length) },
    rows,
  };
}

/** Columnas Usuario / Usuario destino del informe de movimientos de caja. */
export function movimientoCajaRowUsuarios(row: {
  created_by_label?: string | null;
  createdByLabel?: string | null;
  usuario_destino_label?: string | null;
  usuarioDestinoLabel?: string | null;
  usuario_label?: string | null;
}): { usuario: string; usuarioDestino: string } {
  const usuario =
    String(row.created_by_label ?? row.createdByLabel ?? "").trim() || "Sin usuario";
  const usuarioDestino =
    String(
      row.usuario_destino_label ?? row.usuarioDestinoLabel ?? row.usuario_label ?? "",
    ).trim() || "Sin usuario";
  return { usuario, usuarioDestino };
}
